import { supabase } from './supabase';

export interface DashboardStats {
  activePolls: number;
  totalVotes: number;
  totalUsers: number;
}

export interface RecentPoll {
  id: string;
  title: string;
  status: 'active' | 'draft' | 'closed' | null;
  created_at: string | null;
  vote_count: number;
}

export interface RecentActivity {
  id: string;
  type: 'vote' | 'user' | 'poll';
  description: string;
  created_at: string;
}

class DashboardAdminService {
  /**
   * Get dashboard statistics
   */
  async getStats(): Promise<DashboardStats> {
    // Active polls
    const { count: activePolls } = await supabase
      .from('polls')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'active');

    // Total votes
    const { count: totalVotes } = await supabase
      .from('votes')
      .select('*', { count: 'exact', head: true });

    // Total users
    const { count: totalUsers } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true });

    return {
      activePolls: activePolls || 0,
      totalVotes: totalVotes || 0,
      totalUsers: totalUsers || 0,
    };
  }

  /**
   * Get recent polls
   */
  async getRecentPolls(limit: number = 5): Promise<RecentPoll[]> {
    const { data, error } = await supabase
      .from('polls')
      .select(`
        id,
        title,
        status,
        created_at
      `)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    // Get vote counts for each poll
    const pollsWithCounts = await Promise.all(
      (data || []).map(async (poll) => {
        const { count } = await supabase
          .from('votes')
          .select('*', { count: 'exact', head: true })
          .eq('poll_id', poll.id);

        return {
          ...poll,
          vote_count: count || 0,
        };
      })
    );

    return pollsWithCounts;
  }

  /**
   * Get recent activity
   */
  async getRecentActivity(limit: number = 10): Promise<RecentActivity[]> {
    const activities: RecentActivity[] = [];

    // Recent votes
    const { data: votes } = await supabase
      .from('votes')
      .select(`
        id,
        created_at,
        polls(title),
        option_id,
        poll_options(text, contestants(full_name))
      `)
      .order('created_at', { ascending: false })
      .limit(limit);

    (votes || []).forEach((vote: any) => {
      const optionName = vote.poll_options?.contestants?.full_name || vote.poll_options?.text || 'Opción';
      activities.push({
        id: vote.id,
        type: 'vote',
        description: `Nuevo voto en "${vote.polls?.title}" para ${optionName}`,
        created_at: vote.created_at,
      });
    });

    // Recent users
    const { data: users } = await supabase
      .from('profiles')
      .select('id, created_at, display_name, username')
      .order('created_at', { ascending: false })
      .limit(5);

    (users || []).forEach((user: any) => {
      activities.push({
        id: user.id,
        type: 'user',
        description: `Nuevo usuario: ${user.display_name || user.username || 'Anónimo'}`,
        created_at: user.created_at,
      });
    });

    // Recent polls
    const { data: polls } = await supabase
      .from('polls')
      .select('id, created_at, title')
      .order('created_at', { ascending: false })
      .limit(3);

    (polls || []).forEach((poll: any) => {
      activities.push({
        id: poll.id,
        type: 'poll',
        description: `Nueva encuesta: "${poll.title}"`,
        created_at: poll.created_at,
      });
    });

    // Sort by date and limit
    return activities
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, limit);
  }
}

export const dashboardAdminService = new DashboardAdminService();
